import { useState, useEffect, ReactElement } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { motion } from "framer-motion";

// Material
import { IconButton, Stack, SvgIcon, Typography, Box, Card } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import InProgressIcon from "@mui/icons-material/RotateRight";

// Components
import { UserProfilePicture } from "@/Layouts/components/UserProfilePicture";
import { maskPhoneNumber } from "@/Common/helpers/functions";
import { ReactComponent as MailOutlineIcon } from "@Washouts/assets/icons/email.svg";

// Icons
import { ExpandMore, ExpandLess } from "@mui/icons-material";

const titleClassName = "!font-medium !font-sans !text-[12px] !text-default";

type BasicAvatarCardProps = {
	src: string;
	position: string;
	status: string;
	name: string;
	title: string;
	email: string;
	phone: string;
};

export default function BasicAvatarCard(props: Readonly<BasicAvatarCardProps>) {
	const [expanded, setExpanded] = useState(false);
	const [icon, setIcon] = useState<ReactElement | null>(null);
	const [border, setBorder] = useState("");
	const avatarClasses = "!w-[80px] !h-[80px] border-[2.5px] ml-[-20px] !bg-white !absolute";

	useEffect(() => {
		setBorder(twMerge(avatarClasses, "border-neutral-gray"));
		if (props.status) {
			const fixedClasses = "!h-[20px] !text-[16px]";
			if (props.status === "Approved") {
				setIcon(<DoneIcon className={`!text-green-color !pb-[3px] ${fixedClasses}`} />);
				setBorder(twMerge(avatarClasses, "border-green-color"));
			}
			if (props.status === "In Progress") {
				setIcon(<InProgressIcon className={`!text-primary ${fixedClasses}`} />);
			}
			if (props.status === "Pending Review") {
				setIcon(<TimerOutlinedIcon className={`!text-default !pb-[3px] ${fixedClasses}`} />);
			}
		}

		return () => {
			setIcon(null);
			setBorder("");
		};
	}, [props]);

	const maskedPhone = maskPhoneNumber(props.phone);

	const handleExpandCard = () => {
		setExpanded(!expanded);
	};

	const TEXT_LONG_CLASSES = "whitespace-nowrap overflow-hidden text-ellipsis";

	// Animation variants for collapsing/expanding
	const variants = {
		collapsed: { height: 0, opacity: 0, transition: { duration: 0.2 } },
		expanded: { height: "auto", opacity: 1, transition: { duration: 0.2 } },
	};

	return (
		<Stack display={"flex"} direction={"row"} alignItems={"center"} ml={2}>
			<UserProfilePicture src={props.src} iconClasses="text-neutral-gray fill-neutral-gray" className={border} />
			<Card className="!shadow-[0px_4px_8px_0px] !shadow-shadow-gray !rounded-[6px] p-[2px] my-1">
				<Stack
					display={"flex"}
					direction={"row"}
					alignItems={"top"}
					justifyContent={"space-between"}
					gap={1}
					className={clsx({
						"!pb-[2px]": !expanded,
						"!mb-[16px]": !props.status,
					})}
				>
					<Stack display={"flex"} direction={"column"} spacing={"-2px"}>
						<Stack display={"flex"} direction={"column"} width={"256px"} py={"4px"} pl={"68px"} spacing={"-2px"}>
							<Typography
								className={clsx("!text-base !text-default !font-medium !font-montserrat", {
									[TEXT_LONG_CLASSES]: !expanded,
								})}
							>
								{props.position ?? ""}
							</Typography>
							<Stack display={"flex"} direction={"row"} alignItems={"center"}>
								{icon}
								<Typography className={"!text-xs !text-default !font-medium !font-sans"}>{props.status ?? ""}</Typography>
							</Stack>
							<Typography
								className={clsx("!text-sm !text-default !font-medium !font-montserrat", {
									[TEXT_LONG_CLASSES]: !expanded,
									"!mt-[4px]": !props.status,
								})}
							>
								{props.name ?? ""}
							</Typography>
						</Stack>
						<motion.div
							variants={variants}
							initial="collapsed"
							animate={expanded ? "expanded" : "collapsed"}
							className="overflow-hidden"
						>
							<Stack
								display={"flex"}
								direction={"column"}
								width={"256px"}
								pl={"68px"}
								pb={"4px"}
								spacing={"-2px"}
								className={expanded ? "visible" : "collapse"}
							>
								<Typography className={titleClassName}>{props.title ?? ""}</Typography>
								<Stack display={"flex"} direction={"column"}>
									<Stack display={"flex"} direction={"row"} alignItems={"center"}>
										<SvgIcon component={MailOutlineIcon} className="!text-default !h-[16px] mt-[6px] ml-[-4px]" />
										<Typography className={titleClassName}>{props.email ?? ""}</Typography>
									</Stack>
									<Stack display={"flex"} direction={"row"} alignItems={"center"}>
										<SvgIcon component={PhoneIcon} className="!text-default !h-[16px] ml-[-4px]" />
										<Typography className={titleClassName}>{maskedPhone ?? ""}</Typography>
									</Stack>
								</Stack>
							</Stack>
						</motion.div>
					</Stack>
					<Box className={"flex flex-col align-middle justify-start"} component={"div"}>
						<IconButton aria-label="expand" color="default" onClick={handleExpandCard} className="!p-1">
							{expanded ? <ExpandLess /> : <ExpandMore />}
						</IconButton>
					</Box>
				</Stack>
			</Card>
		</Stack>
	);
}
